import { Proyeccion } from '../../../shared/types';
import { calculateCompoundInterest } from '../../../shared/utils/currency-utils';

export class ProyeccionService {
  calculateProyeccion(
    montoInicial: number,
    tasaInteres: number,
    años: number,
    aporteMensual: number = 0
  ): Proyeccion {
    const proyecciones = [];
    
    for (let año = 1; año <= años; año++) {
      const monto = calculateCompoundInterest(
        montoInicial,
        tasaInteres,
        año,
        aporteMensual
      );
      
      proyecciones.push({
        año,
        monto: Math.round(monto * 100) / 100
      });
    }

    return {
      id: `proyeccion-${Date.now()}`,
      montoInicial,
      tasaInteres,
      años,
      aporteMensual,
      proyecciones,
      createdAt: new Date(),
    };
  }

  calculateProyeccionConAportes(
    montoInicial: number,
    tasaInteres: number,
    años: number,
    aporteMensual: number
  ): { año: number; monto: number; aportes: number; intereses: number }[] {
    const proyecciones = [];
    let montoActual = montoInicial;
    let totalAportes = 0;
    
    for (let año = 1; año <= años; año++) {
      const aporteAnual = aporteMensual * 12;
      totalAportes += aporteAnual;
      
      // Calcular interés compuesto
      const montoConInteres = calculateCompoundInterest(
        montoActual,
        tasaInteres,
        1,
        aporteMensual
      );
      
      const intereses = montoConInteres - montoActual - aporteAnual;
      montoActual = montoConInteres;
      
      proyecciones.push({
        año,
        monto: Math.round(montoActual * 100) / 100,
        aportes: Math.round(totalAportes * 100) / 100,
        intereses: Math.round(intereses * 100) / 100
      });
    }

    return proyecciones;
  }

  calculateProyeccionConservadora(
    montoInicial: number,
    años: number,
    aporteMensual: number = 0
  ): Proyeccion {
    return this.calculateProyeccion(montoInicial, 3, años, aporteMensual);
  }

  calculateProyeccionModerada(
    montoInicial: number,
    años: number,
    aporteMensual: number = 0
  ): Proyeccion {
    return this.calculateProyeccion(montoInicial, 6, años, aporteMensual);
  }

  calculateProyeccionAgresiva(
    montoInicial: number,
    años: number,
    aporteMensual: number = 0
  ): Proyeccion {
    return this.calculateProyeccion(montoInicial, 10, años, aporteMensual);
  }

  calculateProyeccionPersonalizada(
    montoInicial: number,
    tasaInteres: number,
    años: number,
    aporteMensual: number = 0
  ): Proyeccion {
    return this.calculateProyeccion(montoInicial, tasaInteres, años, aporteMensual);
  }

  getProyeccionComparativa(
    montoInicial: number,
    años: number,
    aporteMensual: number = 0
  ): {
    conservadora: Proyeccion;
    moderada: Proyeccion;
    agresiva: Proyeccion;
  } {
    return {
      conservadora: this.calculateProyeccionConservadora(montoInicial, años, aporteMensual),
      moderada: this.calculateProyeccionModerada(montoInicial, años, aporteMensual),
      agresiva: this.calculateProyeccionAgresiva(montoInicial, años, aporteMensual),
    };
  }

  calculateTiempoParaMeta(
    montoInicial: number,
    meta: number,
    tasaInteres: number,
    aporteMensual: number = 0
  ): number {
    let montoActual = montoInicial;
    let años = 0;
    
    while (montoActual < meta && años < 100) {
      años++;
      montoActual = calculateCompoundInterest(
        montoActual,
        tasaInteres,
        1,
        aporteMensual
      );
    }
    
    return años;
  }

  calculateAporteNecesario(
    montoInicial: number,
    meta: number,
    tasaInteres: number,
    años: number
  ): number {
    // Fórmula para calcular el aporte mensual necesario
    const tasaMensual = tasaInteres / 100 / 12;
    const meses = años * 12;
    
    if (tasaMensual === 0) {
      return (meta - montoInicial) / meses;
    }
    
    const factor = Math.pow(1 + tasaMensual, meses);
    const aporteMensual = (meta - montoInicial * factor) / ((factor - 1) / tasaMensual);
    
    return Math.max(0, aporteMensual);
  }
}
